import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import PageHero from 'tio-ui/components/page-hero';
import SectionHeader from 'tio-ui/components/section-header';
import Chip from 'tio-ui/components/chip';
import Avatar from 'tio-ui/components/avatar';
import AvatarGroup from 'tio-ui/components/avatar-group';
import {
  PhArrowLeft,
  PhUserCircle,
  PhArrowsOutSimple,
  PhCircle,
  PhSquare,
  PhPalette,
  PhImage,
  PhUsersThree,
  PhLightning,
  PhCode,
} from 'ember-phosphor-icons';

const DEMO_PEOPLE = [
  { name: 'Alice Chen' },
  { name: 'Bob Davis' },
  { name: 'Carol Evans' },
  { name: 'Derek Foster' },
  { name: 'Elena Garcia' },
  { name: 'Frank Hill' },
  { name: 'Grace Ito' },
  { name: 'Hank Jones' },
];

<template>
  {{! template-lint-disable no-bare-strings }}
  <VStack class="w-full min-w-0">
    <div class="mb-4">
      <LinkTo @route="authenticated.reimagined.components" class="tio-demo-link">
        <PhArrowLeft @size={{18}} />
        Back to Components
      </LinkTo>
    </div>

    <PageHero
      @title="Avatar"
      @subtitle="User avatars with auto-generated initials and deterministic colors, plus AvatarGroup for overlapping stacks. Zero Frontile — semantic CSS."
      @icon={{PhUserCircle}}
      @gradient="brand-soft"
      @decoration="dots"
    >
      <:actions>
        <Chip @intent="primary">
          <PhLightning @size={{14}} class="mr-1" />
          Zero Frontile
        </Chip>
      </:actions>
    </PageHero>

    <SectionHeader
      @title="Sizes"
      @subtitle="Five sizes: xs, sm, md, lg, xl. Default is md."
      @icon={{PhArrowsOutSimple}}
    />
    <div class="tio-reimagined-demo-panel">
      <div class="tio-wrap-loose items-end">
        <div class="tio-stack-center gap-2">
          <Avatar @name="Extra Small" @size="xs" />
          <span class="text-xs tio-fg-muted">xs</span>
        </div>
        <div class="tio-stack-center gap-2">
          <Avatar @name="Small Size" @size="sm" />
          <span class="text-xs tio-fg-muted">sm</span>
        </div>
        <div class="tio-stack-center gap-2">
          <Avatar @name="Medium Size" @size="md" />
          <span class="text-xs tio-fg-muted">md</span>
        </div>
        <div class="tio-stack-center gap-2">
          <Avatar @name="Large Size" @size="lg" />
          <span class="text-xs tio-fg-muted">lg</span>
        </div>
        <div class="tio-stack-center gap-2">
          <Avatar @name="Extra Large" @size="xl" />
          <span class="text-xs tio-fg-muted">xl</span>
        </div>
      </div>
    </div>

    <SectionHeader
      @title="Shapes"
      @subtitle="Circle (default) or square with rounded corners."
      @icon={{PhCircle}}
    />
    <div class="tio-reimagined-demo-panel">
      <div class="tio-row-extra-loose flex-wrap">
        <div class="tio-stack-center gap-2">
          <Avatar @name="Jane Doe" @size="lg" @shape="circle" />
          <span class="text-xs tio-fg-muted">circle</span>
        </div>
        <div class="tio-stack-center gap-2">
          <Avatar @name="Jane Doe" @size="lg" @shape="square" />
          <span class="text-xs tio-fg-muted">square</span>
        </div>
      </div>
    </div>

    <SectionHeader
      @title="Initials"
      @subtitle="Derived from @name (first two words) or @firstName / @lastName."
      @icon={{PhCode}}
    />
    <div class="tio-reimagined-demo-panel">
      <div class="tio-row-loose flex-wrap">
        <div class="tio-stack-center gap-2">
          <Avatar @name="Jane Doe" @size="lg" />
          <span class="text-xs tio-fg-muted">@name="Jane Doe"</span>
        </div>
        <div class="tio-stack-center gap-2">
          <Avatar @firstName="Alice" @lastName="Smith" @size="lg" />
          <span class="text-xs tio-fg-muted">@firstName + @lastName</span>
        </div>
        <div class="tio-stack-center gap-2">
          <Avatar @name="Madonna" @size="lg" />
          <span class="text-xs tio-fg-muted">Single name</span>
        </div>
      </div>
    </div>

    <SectionHeader
      @title="Deterministic Colors"
      @subtitle="Each name always maps to the same color from a 16-color on-brand palette. No manual color assignment needed."
      @icon={{PhPalette}}
    />
    <div class="tio-reimagined-demo-panel">
      <div class="tio-row flex-wrap">
        <Avatar @name="Alice Chen" @size="md" />
        <Avatar @name="Bob Davis" @size="md" />
        <Avatar @name="Carol Evans" @size="md" />
        <Avatar @name="Derek Foster" @size="md" />
        <Avatar @name="Elena Garcia" @size="md" />
        <Avatar @name="Frank Hill" @size="md" />
        <Avatar @name="Grace Ito" @size="md" />
        <Avatar @name="Hank Jones" @size="md" />
      </div>
      <p class="text-xs tio-fg-muted mt-3">Same names always produce the same color — stable across
        sessions.</p>
    </div>

    <SectionHeader
      @title="Explicit Color Override"
      @subtitle="Override the auto-generated color with @color."
      @icon={{PhPalette}}
    />
    <div class="tio-reimagined-demo-panel">
      <div class="tio-row flex-wrap">
        <Avatar @name="Jane Doe" @color="tio-avatar-color-0" @size="lg" />
        <Avatar @name="Jane Doe" @color="tio-avatar-color-3" @size="lg" />
        <Avatar @name="Jane Doe" @color="tio-avatar-color-2" @size="lg" />
        <Avatar @name="Jane Doe" @color="tio-bg-warning" @size="lg" />
      </div>
    </div>

    <SectionHeader
      @title="With Image"
      @subtitle="Provide @src to render a photo instead of initials. Uses local or app-hosted images — no external URLs."
      @icon={{PhImage}}
    />
    <div class="tio-reimagined-demo-panel">
      <p class="text-sm tio-fg-muted mb-3">Image avatars are supported via
        <code class="text-xs tio-bg-sunken px-1 py-0.5 rounded">@src</code>. In production this
        renders employee photos from S3. Demo uses initials only to avoid external dependencies.</p>
      <div class="tio-row-loose flex-wrap">
        <Avatar @name="Photo User" @size="md" />
        <Avatar @name="Photo User" @size="lg" />
        <Avatar @name="Photo User" @size="xl" />
      </div>
    </div>

    <SectionHeader
      @title="AvatarGroup"
      @subtitle="Overlapping stack of avatars with automatic +N overflow. Replaces hand-rolled overlap layouts."
      @icon={{PhUsersThree}}
    />
    <div class="tio-reimagined-demo-panel space-y-6">
      <div>
        <p class="tio-text-sm-medium tio-fg-secondary mb-3">Default (max 5)</p>
        <AvatarGroup @people={{DEMO_PEOPLE}} @size="md" />
      </div>
      <div>
        <p class="tio-text-sm-medium tio-fg-secondary mb-3">Max 3</p>
        <AvatarGroup @people={{DEMO_PEOPLE}} @size="md" @max={{3}} />
      </div>
      <div>
        <p class="tio-text-sm-medium tio-fg-secondary mb-3">All visible (max 10)</p>
        <AvatarGroup @people={{DEMO_PEOPLE}} @size="md" @max={{10}} />
      </div>
    </div>

    <SectionHeader
      @title="AvatarGroup Sizes"
      @subtitle="The group inherits the avatar @size."
      @icon={{PhArrowsOutSimple}}
    />
    <div class="tio-reimagined-demo-panel space-y-6">
      <div>
        <p class="tio-text-sm-medium tio-fg-secondary mb-3">Extra small</p>
        <AvatarGroup @people={{DEMO_PEOPLE}} @size="xs" @max={{4}} />
      </div>
      <div>
        <p class="tio-text-sm-medium tio-fg-secondary mb-3">Small</p>
        <AvatarGroup @people={{DEMO_PEOPLE}} @size="sm" @max={{4}} />
      </div>
      <div>
        <p class="tio-text-sm-medium tio-fg-secondary mb-3">Medium</p>
        <AvatarGroup @people={{DEMO_PEOPLE}} @size="md" @max={{4}} />
      </div>
      <div>
        <p class="tio-text-sm-medium tio-fg-secondary mb-3">Large</p>
        <AvatarGroup @people={{DEMO_PEOPLE}} @size="lg" @max={{4}} />
      </div>
    </div>

    <SectionHeader
      @title="AvatarGroup Spacing"
      @subtitle="Control overlap tightness: tight, normal, loose."
      @icon={{PhUsersThree}}
    />
    <div class="tio-reimagined-demo-panel space-y-6">
      <div>
        <p class="tio-text-sm-medium tio-fg-secondary mb-3">Tight</p>
        <AvatarGroup @people={{DEMO_PEOPLE}} @size="md" @max={{6}} @spacing="tight" />
      </div>
      <div>
        <p class="tio-text-sm-medium tio-fg-secondary mb-3">Normal (default)</p>
        <AvatarGroup @people={{DEMO_PEOPLE}} @size="md" @max={{6}} @spacing="normal" />
      </div>
      <div>
        <p class="tio-text-sm-medium tio-fg-secondary mb-3">Loose</p>
        <AvatarGroup @people={{DEMO_PEOPLE}} @size="md" @max={{6}} @spacing="loose" />
      </div>
    </div>

    <SectionHeader
      @title="Thin Ring (Frontile Fix)"
      @subtitle="Frontile Avatar used ring-2 which felt chunky. Reimagined Avatar uses ring-1 for a cleaner look."
      @icon={{PhSquare}}
    />
    <div class="tio-reimagined-demo-panel">
      <div class="tio-row-extra-loose flex-wrap">
        <div class="tio-stack-center gap-2">
          <Avatar @name="Thin Ring" @size="xl" />
          <span class="text-xs tio-fg-muted">ring-1 (reimagined)</span>
        </div>
        <div class="tio-stack-center gap-2">
          <span class="tio-demo-avatar-trigger">TR</span>
          <span class="text-xs tio-fg-muted">ring-2 (old Frontile)</span>
        </div>
      </div>
    </div>
  </VStack>
</template>
